import {Injectable} from '@angular/core';
import {BehaviorSubject, Observable, of, Subject, switchMap} from "rxjs";
import {SortDirection} from "@app/_directives/sortable.directive";
import {SearchResult, State} from "@app/_models/tableOptions";

const compare = (v1: string | number | boolean, v2: string | number | boolean) => {
    if (typeof v1 === 'boolean' && typeof v2 === 'boolean') {
        return (v1 === v2 ? 0 : v1 ? 1 : -1);
    }
    v1 = (v1 ?? "") as string | number | boolean;
    v2 = (v2 ?? "") as string | number | boolean;
    return v1 < v2 ? -1 : v1 > v2 ? 1 : 0;
};

@Injectable({
    providedIn: 'root'
})
export abstract class TableOptionsService<T> {
    protected _search$ = new Subject<void>();
    protected _total$ = new BehaviorSubject<number>(0);
    protected _data$ = new BehaviorSubject<T[]>([]);
    protected _state: State = {
        page: 1,
        pageSize: 100,
        searchTerm: '',
        sortColumn: '',
        sortDirection: '',
    };

    public data: T[] = [];

    protected constructor() {
        this._search$
        .pipe(switchMap(() => this._search()))
        .subscribe((result) => {
            this._data$.next(result.data);
            this._total$.next(result.total);
        });
    }

    abstract matches(element: T, term: string): boolean | undefined;

    refreshDataSource() {
        this._search$.next();
    }

    get data$() {
        return this._data$.asObservable();
    }

    get total$() {
        return this._total$.asObservable();
    }

    get page() {
        return this._state.page;
    }

    get pageSize() {
        return this._state.pageSize;
    }

    get searchTerm() {
        return this._state.searchTerm;
    }

    set page(page: number) {
        this._set({page});
    }

    set pageSize(pageSize: number) {
        this._set({pageSize});
    }

    set searchTerm(searchTerm: string) {
        this._set({searchTerm});
    }

    set sortColumn(sortColumn: string) {
        this._set({sortColumn});
    }

    set sortDirection(sortDirection: SortDirection) {
        this._set({sortDirection});
    }

    private _set(patch: Partial<State>) {
        Object.assign(this._state, patch);
        this._search$.next();
    }

    protected _search(): Observable<SearchResult<T>> {
        const {sortColumn, sortDirection, pageSize, page, searchTerm} = this._state;

        // 1. sort
        let data = this.sort(this.data, sortColumn, sortDirection);

        // 2. filter
        data = data.filter(
            (element) => {
                return this.matches(element, searchTerm);
            }
        );
        const total = data.length;

        // 3. paginate
        data = data.slice((page - 1) * pageSize, (page - 1) * pageSize + pageSize);
        return of({data, total});
    }

    protected sort(elements: T[], column: string, direction: string): T[] {
        return elements.sort((a, b) => {
            const valueA = a[column as keyof typeof a] as string | number | boolean;
            const valueB = b[column as keyof typeof b] as string | number | boolean;
            const result = compare(valueA, valueB);
            return direction === 'asc' ? result : -result;
        });
    }
}
